/*
 * Filters panel (SCR-002) — Phase A1. No dependencies. Modal drawer (>= 600 px) / bottom sheet (< 600 px) with the facet groups the
 * gateway returns: color, size and brand (multiple choice, checkboxes), price (single choice, radios with a "any price" option) and
 * "only available" (one checkbox). Pure helpers are exported for node:test:
 *   toggleValue(list, value, on)   immutable add/remove of a value in a list
 *   countFilters(filters)          number of applied filters (each selected value counts once; price and available count 1)
 *   normalizeFilters(filters)      { color[], size[], brand[], price, available } with every key present
 * Host API: setFacets(facets), setSelected(filters), open(trigger), close(). Events (bubbling): `nc-filters:apply` {filters},
 * `nc-filters:close` {trigger}. The draft lives only inside the panel: Escape / scrim / close discard it, "Ver resultados" applies.
 * Focus: first control on open, Tab trapped, page behind inert and scroll-locked, focus returns to the trigger on close.
 */
(function () {
  var KEYS = ['color', 'size', 'brand'];

  function normalizeFilters(f) {
    f = f || {};
    return {
      color: (f.color || []).slice(),
      size: (f.size || []).slice(),
      brand: (f.brand || []).slice(),
      price: f.price || '',
      available: !!f.available
    };
  }

  function toggleValue(list, value, on) {
    var out = (list || []).filter(function (v) { return v !== value; });
    if (on) out.push(value);
    return out;
  }

  // checked state of one control; `available` is a boolean (not a list) and price is a single value
  function isChecked(draft, key, kind, value) {
    if (kind === 'radio') return (draft.price || '') === value;
    if (key === 'available') return !!draft.available;
    return (draft[key] || []).indexOf(value) >= 0;
  }

  function countFilters(f) {
    f = normalizeFilters(f);
    return f.color.length + f.size.length + f.brand.length + (f.price ? 1 : 0) + (f.available ? 1 : 0);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { isChecked: isChecked, normalizeFilters: normalizeFilters, toggleValue: toggleValue, countFilters: countFilters };
  if (typeof customElements === 'undefined' || customElements.get('nc-filter-panel')) return;

  var FOCUSABLE = 'button:not(:disabled):not([aria-disabled="true"]), input:not(:disabled), [href], [tabindex]:not([tabindex="-1"])';
  var uid = 0;

  class NcFilterPanel extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.nc-filter-panel__panel');
      this.body = this.querySelector('[data-filter-body]');
      this.facets = {};
      this.selected = normalizeFilters();
      this.draft = normalizeFilters();
      this.trigger = null;
      this.querySelector('.nc-filter-panel__scrim').addEventListener('click', this.close.bind(this));
      this.querySelector('.nc-filter-panel__close').addEventListener('click', this.close.bind(this));
      this.dialog.addEventListener('keydown', this.onKeydown.bind(this));
      this.body.addEventListener('change', this.onChange.bind(this));
      this.querySelector('[data-filter-clear] .nc-button').addEventListener('click', this.onClear.bind(this));
      this.querySelector('[data-filter-apply] .nc-button').addEventListener('click', this.onApply.bind(this));
    }

    get isOpen() { return !this.hidden && this.getAttribute('data-state') !== 'closing'; }

    setFacets(facets) { this.facets = facets || {}; if (this.isOpen) this.render(); }
    setSelected(filters) { this.selected = normalizeFilters(filters); }

    legend(key) { return this.getAttribute('data-legend-' + key) || key; }

    group(key, kind, items) {
      var self = this;
      var fs = document.createElement('fieldset');
      fs.className = 'nc-filter-panel__group';
      fs.setAttribute('data-group', key);
      var lg = document.createElement('legend');
      lg.className = 'nc-filter-panel__legend';
      lg.textContent = this.legend(key);
      fs.appendChild(lg);
      items.forEach(function (it) {
        var id = 'ncf-' + (++uid);
        var row = document.createElement('label');
        row.className = 'nc-filter-panel__row';
        row.setAttribute('for', id);
        var input = document.createElement('input');
        input.type = kind;
        input.id = id;
        input.className = 'nc-filter-panel__input';
        input.name = key;
        input.value = it.value;
        input.checked = isChecked(self.draft, key, kind, it.value);
        var box = document.createElement('span');
        box.className = 'nc-filter-panel__box nc-filter-panel__box--' + kind;
        box.setAttribute('aria-hidden', 'true');
        var text = document.createElement('span');
        text.className = 'nc-filter-panel__text';
        text.textContent = it.label;
        row.appendChild(input);
        row.appendChild(box);
        row.appendChild(text);
        if (typeof it.count === 'number') {
          var n = document.createElement('span');
          n.className = 'nc-filter-panel__count';
          n.textContent = '(' + it.count + ')';
          row.appendChild(n);
        }
        fs.appendChild(row);
      });
      return fs;
    }

    render() {
      var self = this;
      var f = this.facets || {};
      this.body.textContent = '';
      KEYS.forEach(function (k) {
        var list = (f[k] || []).slice();
        // a selected value that the facets no longer list stays visible, so it can always be unchecked
        self.draft[k].forEach(function (v) { if (!list.some(function (x) { return x.value === v; })) list.push({ value: v, label: v }); });
        if (list.length) self.body.appendChild(self.group(k, 'checkbox', list));
      });
      var prices = (f.price || []).slice();
      if (prices.length) {
        prices.unshift({ value: '', label: this.getAttribute('data-label-any-price') || '' });
        this.body.appendChild(this.group('price', 'radio', prices));
      }
      var av = this.group('available', 'checkbox', [{ value: 'available', label: this.getAttribute('data-label-available') || '' }]);
      this.body.appendChild(av);
      if (!this.body.querySelector('[data-group]:not([data-group="available"])')) {
        var p = document.createElement('p');
        p.className = 'nc-filter-panel__empty';
        p.textContent = this.getAttribute('data-label-empty') || '';
        this.body.insertBefore(p, this.body.firstChild);
      }
    }

    onChange(event) {
      var input = event.target;
      if (!input || !input.name) return;
      if (input.name === 'price') this.draft.price = input.value;
      else if (input.name === 'available') this.draft.available = input.checked;
      else this.draft[input.name] = toggleValue(this.draft[input.name], input.value, input.checked);
    }

    onClear() {
      this.draft = normalizeFilters();
      this.render();
      var first = this.body.querySelector('input');
      if (first) first.focus();
    }

    onApply() {
      var filters = normalizeFilters(this.draft);
      this.pendingApply = filters;
      this.close();
    }

    open(trigger) {
      if (this.isOpen) return;
      clearTimeout(this.timer);
      this.trigger = trigger || document.activeElement;
      this.draft = normalizeFilters(this.selected);
      this.render();
      this.hidden = false;
      this.removeAttribute('data-state');
      this.pendingApply = null;
      document.documentElement.setAttribute('data-nc-scroll-lock', '');
      this.setBackgroundInert(true);
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      var first = this.body.querySelector('input') || this.querySelector('.nc-filter-panel__close');
      first.focus();
    }

    close() {
      if (this.hidden || this.getAttribute('data-state') === 'closing') return;
      var self = this;
      var trigger = this.trigger;
      var apply = this.pendingApply;
      this.pendingApply = null;
      this.setAttribute('data-state', 'closing');
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.timer = setTimeout(function () {
        self.hidden = true;
        self.removeAttribute('data-state');
        document.documentElement.removeAttribute('data-nc-scroll-lock');
        self.setBackgroundInert(false);
        if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'false');
        // restore focus to the opener first; the host may move it afterwards (e.g. to the results heading)
        var visible = trigger && trigger.isConnected && !trigger.closest('[hidden]') && trigger.getClientRects().length > 0;
        if (visible) trigger.focus();
        if (apply) self.dispatchEvent(new CustomEvent('nc-filters:apply', { bubbles: true, detail: { filters: apply } }));
        self.dispatchEvent(new CustomEvent('nc-filters:close', { bubbles: true, detail: { trigger: trigger } }));
      }, reduced ? 0 : 180);
    }

    // everything outside the panel becomes inert: walk up from the panel and inert each ancestor's other children
    setBackgroundInert(on) {
      var node = this;
      while (node && node !== document.documentElement) {
        var parent = node.parentElement;
        if (!parent) break;
        Array.prototype.forEach.call(parent.children, function (el) {
          if (el === node || el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK' || el.tagName === 'TEMPLATE') return;
          if (on) {
            if (!el.hasAttribute('inert')) { el.setAttribute('inert', ''); el.setAttribute('data-nc-filters-inert', ''); }
          } else if (el.hasAttribute('data-nc-filters-inert')) {
            el.removeAttribute('inert');
            el.removeAttribute('data-nc-filters-inert');
          }
        });
        node = parent;
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      var stops = Array.prototype.slice.call(this.dialog.querySelectorAll(FOCUSABLE)).filter(function (el) { return el.getClientRects().length > 0; });
      if (!stops.length) return;
      var i = stops.indexOf(document.activeElement);
      var next = i < 0 ? (event.shiftKey ? stops.length - 1 : 0) : (i + (event.shiftKey ? -1 : 1) + stops.length) % stops.length;
      event.preventDefault();
      stops[next].focus();
    }
  }

  customElements.define('nc-filter-panel', NcFilterPanel);
})();
